/** Browser keepalive fetch bodies are capped around 64 KiB. */
export const KEEPALIVE_BODY_LIMIT = 60000;

export type BeaconBodyPrep = {
  body: string;
  /** false when the body cannot be sent even after shedding recordingEvents */
  sendable: boolean;
};

/**
 * Prepare a beacon body for keepalive fetch. Oversize payloads shed
 * recordingEvents once; if they still exceed the limit, mark unsendable so
 * callers can retry later instead of silently dropping data.
 */
export function prepareBeaconBody(rawBody: string): BeaconBodyPrep {
  if (rawBody.length <= KEEPALIVE_BODY_LIMIT) {
    return { body: rawBody, sendable: true };
  }

  try {
    const parsed = JSON.parse(rawBody) as { recordingEvents?: unknown };
    if (
      Array.isArray(parsed.recordingEvents) &&
      parsed.recordingEvents.length > 0
    ) {
      parsed.recordingEvents = [];
      const reduced = JSON.stringify(parsed);
      if (reduced.length <= KEEPALIVE_BODY_LIMIT) {
        return { body: reduced, sendable: true };
      }
    }
  } catch {
    // Invalid JSON — cannot shed; treat as unsendable below.
  }

  return { body: rawBody, sendable: false };
}

/**
 * Fire-and-forget POST via fetch+keepalive (survives page unload).
 * Avoids navigator.sendBeacon which forces credentials:"include"
 * and breaks CORS when the server returns Access-Control-Allow-Origin:*.
 */
export function beaconFetchPost(
  endpoint: string,
  apiKey: string,
  rawBody: string
): boolean {
  const prepared = prepareBeaconBody(rawBody);
  if (!prepared.sendable) {
    return false;
  }

  try {
    fetch(endpoint, {
      method: "POST",
      body: prepared.body,
      keepalive: true,
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}
